import type {
  AccountDetail,
  InlineSpouseInput,
  Personnel,
  PersonnelAddress,
  PersonnelAddressInput,
} from "@/lib/brokerAccounts";
import {
  buildAccountPatch,
  isOwner,
  newPersonRow,
  removePerson,
  valuesFromDetail,
  type AccountPatchResult,
  type PersonRow,
} from "@/lib/accountRecordForm";
import { parseDecimal } from "@/lib/accountWrite";
import {
  EMPTY_ADDRESS,
  formatAddress,
  isCompleteAddress,
  trimAddress,
  type AddressValue,
} from "@/lib/address";
import { maritalStatusLabel, spouseSigns } from "@/lib/bondRoster";
import { readAccountRequirements } from "@/lib/accountRequirements";
import { formatSsn, maskedSsn } from "@/lib/personnelPii";
import {
  formatE164ForDisplay,
  phoneRowErrors,
  primaryPhone,
  toPhoneInputs,
  toPhoneValues,
  type PhoneValue,
} from "@/lib/phone";

// BRK-S408 — the Surety account's Owners & principals tab: who the table lists
// and as what, and the whole-roster account PUT each add, edit and removal
// sends.
//
// ── THE RELATIONSHIP IS READ, NEVER CHOSEN ──────────────────────────────────
//
// PMS stores no "relationship". It builds the GIA's signers from three facts
// it already holds (PMS-S606), and the table names people by the same three:
//
//   Principal Owner — the person `principalOwner` names. PMS resolves it
//     server-side (lib/owner-primacy.ts) and publishes the row's
//     `personnelId` (PMS-S840), so this finds the owner the server named by
//     id; it never ranks shares itself (two 50/50 owners have no largest
//     shareholder — PMS-S238).
//   Owner — anyone else holding a share above 0.
//   Spouse — someone holding no share who is linked to an owner by `spouseId`.
//   Non-Owner Indemnitor — someone holding no share whom PMS marks as signing
//     as a creditworthy guarantor (`nonOwnerIndemnitor`, PMS-S846,
//     BRK-S434). The one relationship the dialog CHOOSES, because it is the
//     one PMS stores as a fact of its own.
//
// Everyone else on the account (a site superintendent, an accounting contact)
// is not an indemnitor and is not listed — but still goes back out on every
// write, because a person the roster PUT leaves out is removed (PMS-S551).
//
export type OwnerRelationship =
  | "principal_owner"
  | "owner"
  | "spouse"
  | "non_owner_indemnitor";

export const OWNER_RELATIONSHIP_LABELS: Record<OwnerRelationship, string> = {
  principal_owner: "Principal Owner",
  owner: "Owner",
  spouse: "Spouse",
  non_owner_indemnitor: "Non-Owner Indemnitor",
};

/** The wireframe's ⓘ on the Relationship column, in words. */
export const OWNER_RELATIONSHIP_HELP =
  "Principal Owner and Owner hold a share of the business; a Spouse is an owner’s husband or wife; a Non-Owner Indemnitor owns none of it but signs the indemnity agreement as a creditworthy guarantor.";

/** What the dialog lets the broker choose: the rest PMS derives. */
export type OwnerChoice = "owner" | "non_owner_indemnitor";

/** One row of the table, display-ready. */
export interface OwnerRow {
  id: string;
  relationship: OwnerRelationship;
  name: string;
  title: string | null;
  /** Marital status and spouse for an owner; "Spouse of …" for a spouse. */
  sub: string | null;
  /** "75%", or "0%" for a spouse or a non-owner indemnitor. */
  share: string;
  legalAddress: string | null;
  /** Only when it differs from the legal address. */
  mailingAddress: string | null;
  /** "•••-••-1234", "On file" (no last four stored), or null. */
  ssn: string | null;
  email: string | null;
  phone: string | null;
  /** A `complete_indemnitors` blocker names this person. */
  needsDetails: boolean;
}

function shareOfPerson(p: Personnel): number {
  return parseDecimal(p.ownershipPercentage) ?? 0;
}

function holdsShare(p: Personnel): boolean {
  return shareOfPerson(p) > 0;
}

export function fullName(p: Pick<Personnel, "firstName" | "lastName">): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

/**
 * The owner `principalOwner` names: the row its `personnelId` names when that
 * row is on the roster. Without an id (a row PMS has not stored yet), the
 * first owner, in the order PMS served them, with that name and share — PMS's
 * own tie-break is that same served order.
 */
function principalId(detail: AccountDetail): string | null {
  const named = detail.principalOwner;
  if (!named) return null;
  const roster = detail.personnel ?? [];
  if (named.personnelId !== null) {
    return roster.some((p) => p.id === named.personnelId)
      ? named.personnelId
      : null;
  }
  const match = roster.find(
    (p) =>
      holdsShare(p) &&
      fullName(p) === named.name.trim() &&
      shareOfPerson(p) === named.ownershipPercentage,
  );
  return match?.id ?? null;
}

/** A person's own address of `type`, the primary one first. */
function addressOf(
  p: Personnel | undefined,
  type: PersonnelAddress["type"],
): { entry: PersonnelAddress; index: number } | null {
  const all = (p?.addresses ?? []).map((entry, index) => ({ entry, index }));
  const ofType = all.filter((a) => a.entry.type === type);
  return ofType.find((a) => a.entry.isPrimary) ?? ofType[0] ?? null;
}

function toValue(a: PersonnelAddress | null | undefined): AddressValue {
  if (!a) return { ...EMPTY_ADDRESS };
  return {
    street: a.street,
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(a.addressLine2 ? { addressLine2: a.addressLine2 } : {}),
  };
}

function isBlankAddress(a: AddressValue): boolean {
  return ![a.street, a.city, a.state, a.zip, a.addressLine2 ?? ""].some((v) =>
    v.trim(),
  );
}

function sameAddress(a: AddressValue, b: AddressValue): boolean {
  const norm = (v: string | undefined) => (v ?? "").trim().toLowerCase();
  return (["street", "addressLine2", "city", "state", "zip"] as const).every(
    (k) => norm(a[k]) === norm(b[k]),
  );
}

/** The spouse an owner is linked to, read from either side of the link. */
function spouseOf(
  owner: Personnel,
  people: readonly Personnel[],
): Personnel | undefined {
  return (
    people.find((p) => p.id === owner.spouseId) ??
    people.find((p) => p.spouseId === owner.id)
  );
}

/** The owner a spouse-only seat is married to. */
function ownerOfSpouse(
  spouse: Personnel,
  people: readonly Personnel[],
): Personnel | undefined {
  if (holdsShare(spouse)) return undefined;
  const owner =
    people.find((p) => p.id === spouse.spouseId) ??
    people.find((p) => p.spouseId === spouse.id);
  return owner && holdsShare(owner) ? owner : undefined;
}

function ssnOf(p: Personnel): string | null {
  // `pii` is required on the read, but a person the dev stub just wrote may
  // not carry it yet — read defensively rather than white-screen.
  const pii: Personnel["pii"] | undefined = p.pii;
  const ssn = pii?.ssn;
  if (ssn?.last4) return maskedSsn(ssn.last4);
  return ssn?.onFile ? "On file" : null;
}

function phoneOf(p: Personnel): string | null {
  const primary = primaryPhone(p.phones);
  if (!primary) return null;
  return primary.e164
    ? formatE164ForDisplay(primary.e164, primary.extension)
    : primary.number;
}

function maritalLine(owner: Personnel, spouse: Personnel | undefined): string {
  const label = maritalStatusLabel(owner.maritalStatus);
  if (label === null) return "Marital status not recorded";
  if (spouseSigns(owner.maritalStatus) !== true) return label;
  return spouse
    ? `${label} · Spouse: ${fullName(spouse) || "name needed"}`
    : `${label} · Spouse not recorded`;
}

/**
 * The people a GIA blocker names, when the GIA's next step is "Complete the
 * owners’ details" (`complete_indemnitors`). PMS decides who is incomplete;
 * this portal never re-derives it from which fields look empty.
 *
 * Empty when PMS publishes no requirements, when the GIA's action is anything
 * else, or when the row is one this build does not fully understand.
 */
export function peopleNeedingDetails(raw: unknown): ReadonlySet<string> {
  const gia = readAccountRequirements(raw)?.find((r) => r.kind === "gia");
  if (!gia?.understood || gia.action?.kind !== "complete_indemnitors") {
    return new Set();
  }
  const list = record(raw)?.requirements;
  const wire = (Array.isArray(list) ? list : [])
    .map(record)
    .find((r) => r?.kind === "gia");
  const ids = new Set<string>();
  for (const b of Array.isArray(wire?.blocking) ? wire.blocking : []) {
    const id = record(b)?.personnelId;
    if (typeof id === "string" && id.trim()) ids.add(id.trim());
  }
  return ids;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** The table: the principal owner first, then the other owners in served
 *  order, each owner's spouse directly under them. */
export function ownerRows(detail: AccountDetail): OwnerRow[] {
  const people = detail.personnel ?? [];
  const principal = principalId(detail);
  const needs = peopleNeedingDetails(detail.requirements);
  const owners = people.filter(holdsShare);
  const ordered = [
    ...owners.filter((p) => p.id === principal),
    ...owners.filter((p) => p.id !== principal),
  ];

  const rowFor = (
    p: Personnel,
    relationship: OwnerRelationship,
    sub: string | null,
    share: string,
  ): OwnerRow => {
    const legal = addressOf(p, "home")?.entry ?? null;
    const mailing = addressOf(p, "mailing")?.entry ?? null;
    return {
      id: p.id,
      relationship,
      name: fullName(p) || "Name needed",
      title: p.title?.trim() || null,
      sub,
      share,
      legalAddress: legal ? formatAddress(legal) : null,
      mailingAddress:
        mailing && !(legal && sameAddress(toValue(mailing), toValue(legal)))
          ? formatAddress(mailing)
          : null,
      ssn: ssnOf(p),
      email: p.email?.trim() || null,
      phone: phoneOf(p),
      needsDetails: needs.has(p.id),
    };
  };

  const ownerRowsList = ordered.flatMap((owner) => {
    const spouse = spouseOf(owner, people);
    const rows = [
      rowFor(
        owner,
        owner.id === principal ? "principal_owner" : "owner",
        maritalLine(owner, spouse),
        `${shareOfPerson(owner)}%`,
      ),
    ];
    if (spouse && !holdsShare(spouse)) {
      rows.push(rowFor(spouse, "spouse", `Spouse of ${fullName(owner)}`, "0%"));
    }
    return rows;
  });

  // Non-owner indemnitors after the owners and their spouses.
  const listed = new Set(ownerRowsList.map((r) => r.id));
  const indemnitors = people
    .filter((p) => p.nonOwnerIndemnitor === true && !holdsShare(p))
    .filter((p) => !listed.has(p.id))
    .map((p) => rowFor(p, "non_owner_indemnitor", null, "0%"));
  return [...ownerRowsList, ...indemnitors];
}

// ── The add / edit dialog ───────────────────────────────────────────────────

/** The key a person added from the dialog carries until PMS gives them an id. */
export const NEW_OWNER_KEY = "new-owner";

/**
 * What the add/edit dialog edits. `people` is the WHOLE roster as the
 * Contacts tab models it (`PersonRow`): marital status and the spouse change
 * two rows at once (`setMaritalStatus`, `linkSpouse`), and the write sends
 * everyone.
 */
export interface OwnerDraft {
  /** The row being edited — a loaded person's id, or `NEW_OWNER_KEY`. */
  key: string;
  isNew: boolean;
  people: PersonRow[];
  /** Their own legal (home) address. */
  legal: AddressValue;
  /** "Mailing address same as legal": no separate mailing address. */
  mailingSame: boolean;
  mailing: AddressValue;
  /** For a spouse, their owner's legal address, offered as theirs. */
  sameAsOwner: { name: string; address: AddressValue } | null;
  legalSameAsOwner: boolean;
  /** Owner or Non-Owner Indemnitor; null for a spouse, whom the owner's
   *  marital status and link make one. */
  relationship: OwnerChoice | null;
  /** WRITE-ONLY: typed here, sent once, never pre-filled or read back. */
  ssn: string;
  /** A spouse added inline: what the names-and-email fields don't ask. */
  newSpouse: {
    phones: PhoneValue[];
    legalSameAsOwner: boolean;
    legal: AddressValue;
  };
}

/** A draft for `id`, or for a new owner when `id` is null. */
export function ownerDraftFor(
  detail: AccountDetail,
  id: string | null,
): OwnerDraft {
  const people = valuesFromDetail(detail).personnel;
  const newSpouse = {
    phones: toPhoneValues([]),
    legalSameAsOwner: true,
    legal: { ...EMPTY_ADDRESS },
  };
  const person = id
    ? (detail.personnel ?? []).find((p) => p.id === id)
    : undefined;
  if (!person) {
    return {
      key: NEW_OWNER_KEY,
      isNew: true,
      people: [...people, newPersonRow(NEW_OWNER_KEY)],
      legal: { ...EMPTY_ADDRESS },
      mailingSame: true,
      mailing: { ...EMPTY_ADDRESS },
      sameAsOwner: null,
      legalSameAsOwner: false,
      relationship: "owner",
      ssn: "",
      newSpouse,
    };
  }
  const legal = toValue(addressOf(person, "home")?.entry);
  const mailingEntry = addressOf(person, "mailing")?.entry;
  const mailing = toValue(mailingEntry);
  const owner = ownerOfSpouse(person, detail.personnel ?? []);
  const ownerLegal = owner ? addressOf(owner, "home")?.entry : undefined;
  const sameAsOwner =
    owner && ownerLegal
      ? { name: fullName(owner), address: toValue(ownerLegal) }
      : null;
  return {
    key: person.id,
    isNew: false,
    people,
    legal,
    mailingSame: !mailingEntry || sameAddress(mailing, legal),
    mailing,
    sameAsOwner,
    legalSameAsOwner:
      sameAsOwner !== null &&
      (isBlankAddress(legal) || sameAddress(legal, sameAsOwner.address)),
    relationship: owner
      ? null
      : person.nonOwnerIndemnitor === true && !holdsShare(person)
        ? "non_owner_indemnitor"
        : "owner",
    ssn: "",
    newSpouse,
  };
}

/** An address the way a person's `addresses[]` takes it. The county has no
 *  input here, so it rides from a loaded address that is still the same
 *  street, city and state, and is dropped once the address moved. */
function personAddressInput(
  value: AddressValue,
  type: PersonnelAddress["type"],
  isPrimary: boolean,
  loaded: readonly (PersonnelAddress | null | undefined)[],
): PersonnelAddressInput {
  const t = trimAddress(value);
  const same = (a: string, b: string) =>
    a.trim().toLowerCase() === b.trim().toLowerCase();
  const county = loaded.find(
    (a) =>
      a?.county &&
      same(a.street, t.street) &&
      same(a.city, t.city) &&
      same(a.state, t.state),
  )?.county;
  return {
    type,
    isPrimary,
    street: t.street,
    ...(t.addressLine2 ? { addressLine2: t.addressLine2 } : {}),
    city: t.city,
    state: t.state,
    zip: t.zip,
    ...(county ? { county } : {}),
  };
}

const address = (label: string) =>
  `Complete the ${label} (street, city, state and ZIP).`;

/**
 * The PUT a dialog save sends: the whole `personnel[]`, everyone else carried
 * back out untouched (`buildAccountPatch`'s rules, BRK-S223), the edited
 * person's addresses replaced, and a new spouse created inline.
 */
export function buildOwnerSave(
  detail: AccountDetail,
  draft: OwnerDraft,
): AccountPatchResult {
  const errors: string[] = [];
  const row = draft.people.find((p) => p.key === draft.key);
  if (!row) return { input: null, errors: ["This person is no longer here."] };

  if (!row.firstName.trim() || !row.lastName.trim()) {
    errors.push("Enter their first and last name.");
  }
  const indemnitor = draft.relationship === "non_owner_indemnitor";
  if (draft.isNew && !indemnitor && !isOwner(row)) {
    errors.push("Enter their ownership share — an owner holds more than 0%.");
  }
  const ssnDigits = draft.ssn.replace(/\D/g, "");
  if (draft.ssn.trim() && ssnDigits.length !== 9) {
    errors.push("Enter the SSN as 9 digits.");
  }
  const legal =
    draft.sameAsOwner && draft.legalSameAsOwner
      ? draft.sameAsOwner.address
      : draft.legal;
  const legalBlank = isBlankAddress(legal);
  if (!legalBlank && !isCompleteAddress(legal)) {
    errors.push(address("legal address"));
  }
  if (!draft.mailingSame && !isCompleteAddress(draft.mailing)) {
    errors.push(address("mailing address"));
  }
  if (phoneRowErrors(row.phones).some((e) => e !== null)) {
    errors.push("Check their phone number.");
  }

  const newSpouse = row.spouse.kind === "new" ? draft.newSpouse : null;
  const spouseLegal = newSpouse
    ? newSpouse.legalSameAsOwner
      ? legal
      : newSpouse.legal
    : null;
  if (newSpouse && spouseLegal) {
    if (
      !newSpouse.legalSameAsOwner &&
      !isBlankAddress(spouseLegal) &&
      !isCompleteAddress(spouseLegal)
    ) {
      errors.push(address("spouse’s legal address"));
    }
    if (phoneRowErrors(newSpouse.phones).some((e) => e !== null)) {
      errors.push("Check the spouse’s phone number.");
    }
  }

  // The edited person's addresses: their legal and mailing ones replaced, any
  // other (previous, other) carried as it was.
  const person = (detail.personnel ?? []).find((p) => p.id === row.id);
  const legalAt = addressOf(person, "home");
  const mailingAt = addressOf(person, "mailing");
  const owner = person ? ownerOfSpouse(person, detail.personnel ?? []) : null;
  const ownerLegal = owner ? addressOf(owner, "home")?.entry : undefined;
  const kept = (row.carried?.addresses ?? []).filter(
    (_, i) => i !== legalAt?.index && i !== mailingAt?.index,
  );
  const addresses: PersonnelAddressInput[] = [
    ...kept,
    ...(legalBlank
      ? []
      : [
          personAddressInput(
            legal,
            "home",
            legalAt?.entry.isPrimary ?? !kept.some((a) => a.isPrimary),
            [legalAt?.entry, ownerLegal],
          ),
        ]),
    ...(draft.mailingSame
      ? []
      : [
          personAddressInput(
            draft.mailing,
            "mailing",
            mailingAt?.entry.isPrimary ?? false,
            [mailingAt?.entry],
          ),
        ]),
  ];
  const hadAddresses = (person?.addresses ?? []).length > 0;
  const people = draft.people.map((p) =>
    p.key === draft.key
      ? {
          ...p,
          // A non-owner indemnitor holds no share, and nothing of an
          // owner's: their marital status puts no spouse on the agreement.
          ...(indemnitor ? { share: "" } : {}),
          carried: {
            ...p.carried,
            addresses: addresses.length || hadAddresses ? addresses : undefined,
            // Explicit either way: the partial PUT keeps an omitted field.
            ...(draft.relationship === null
              ? {}
              : { nonOwnerIndemnitor: indemnitor }),
            // Blank keeps what PMS holds; typed goes out once, formatted.
            ...(ssnDigits.length === 9 ? { ssn: formatSsn(ssnDigits) } : {}),
          },
        }
      : p,
  );

  const patch = roster(detail, people);
  const all = [...errors, ...patch.errors];
  if (all.length || !patch.input?.personnel)
    return { input: null, errors: all };

  // A new spouse's phone and address ride the inline branch with their names.
  if (newSpouse && spouseLegal) {
    const named = people.filter((p) => p.firstName.trim() || p.lastName.trim());
    const entry =
      patch.input.personnel[named.findIndex((p) => p.key === row.key)];
    if (entry?.spouse && "firstName" in entry.spouse) {
      const phones = toPhoneInputs(newSpouse.phones);
      const spouse: InlineSpouseInput = {
        ...entry.spouse,
        ...(phones.length ? { phones } : {}),
        ...(isCompleteAddress(spouseLegal)
          ? {
              addresses: [
                personAddressInput(spouseLegal, "home", true, [legalAt?.entry]),
              ],
            }
          : {}),
      };
      entry.spouse = spouse;
    }
  }
  return patch;
}

/**
 * The whole roster as one write. The account record diffs what changed and
 * omits an untouched roster; here the roster IS the write, so it always goes
 * out — an address-only edit lives in the carried bag the diff never sees.
 */
function roster(
  detail: AccountDetail,
  people: PersonRow[],
): AccountPatchResult {
  const initial = valuesFromDetail(detail);
  return buildAccountPatch(
    { ...initial, personnel: [] },
    { ...initial, personnel: people },
    detail,
  );
}

// ── Removal ─────────────────────────────────────────────────────────────────

export interface OwnerRemoval {
  name: string;
  /** Removed with them: their spouse, when the spouse holds no share. */
  spouse: { id: string; name: string } | null;
}

/** What removing `id` takes off the account, or null when they are gone. */
export function removalOf(
  detail: AccountDetail,
  id: string,
): OwnerRemoval | null {
  const people = detail.personnel ?? [];
  const person = people.find((p) => p.id === id);
  if (!person) return null;
  const spouse = holdsShare(person) ? spouseOf(person, people) : undefined;
  return {
    name: fullName(person) || "this person",
    spouse:
      spouse && !holdsShare(spouse)
        ? { id: spouse.id, name: fullName(spouse) || "their spouse" }
        : null,
  };
}

/** The roster without `id` (and the spouse `removalOf` names), every
 *  marriage the list recorded with them ended (`removePerson`). */
export function buildOwnerRemoval(
  detail: AccountDetail,
  id: string,
): AccountPatchResult {
  const removal = removalOf(detail, id);
  let people = removePerson(valuesFromDetail(detail).personnel, id);
  if (removal?.spouse) people = removePerson(people, removal.spouse.id);
  return roster(detail, people);
}
